import type { CSSProperties } from 'react';
import { Cross2Icon } from '@radix-ui/react-icons';
import { useBoardPanelDrag } from './useBoardPanelDrag';
import type { FoxKifuAiAccountIdentity } from '@wx-weiqi/contracts';
import { accountLabel } from '../stores/ai-account-selection';

import {
  aiCandidateAppearance,
  formatAiScoreLead,
  formatAiVisits,
  type AiCandidatePresentation,
} from '../domain/ai-candidate-presentation';

interface AiCandidatePanelProps {
  readonly account?: FoxKifuAiAccountIdentity | null;
  readonly onChangeAccount?: () => void;
  readonly candidates: readonly AiCandidatePresentation[];
  readonly selectedRank: number | null;
  readonly variationPending: boolean;
  readonly sharingStage?: boolean;
  readonly preserveWidth?: boolean;
  readonly onSelect: (rank: number) => void;
  readonly onClose?: () => void;
}

export function AiCandidatePanel({
  account,
  onChangeAccount,
  candidates,
  selectedRank,
  variationPending,
  sharingStage = false,
  preserveWidth = false,
  onSelect,
  onClose,
}: AiCandidatePanelProps) {
  const panel = useBoardPanelDrag({ preserveWidth });

  return (
    <aside
      className={`ai-candidate-panel${sharingStage ? ' is-sharing-stage' : ''}`}
      ref={panel.panelRef}
      style={panel.style}
      aria-label="AI推荐点数据"
    >
      <header {...panel.dragHandlers}>
        <span>
          <strong>AI 推荐点</strong>
          <small>拖动此处移动</small>
        </span>
        <em>{variationPending ? '变化加载中…' : '点击下法查看变化'}</em>
        {onClose ? (
          <button
            type="button"
            className="ai-candidate-close"
            aria-label="关闭AI推荐点"
            onClick={onClose}
          >
            <Cross2Icon aria-hidden="true" />
          </button>
        ) : null}
        {panel.floating ? (
          <button type="button" onClick={panel.resetPosition}>
            归位
          </button>
        ) : null}
      </header>
      {account ? (
        <div className="ai-account-current">
          <span>当前账号：{accountLabel(account)}</span>
          {onChangeAccount ? (
            <button type="button" onClick={onChangeAccount}>
              切换账号
            </button>
          ) : null}
        </div>
      ) : null}
      <div className="ai-candidate-table" role="table" aria-label="AI推荐下法明细">
        <div className="ai-candidate-table-head" role="row">
          <span role="columnheader">序号</span>
          <span role="columnheader">胜率</span>
          <span role="columnheader">目差</span>
          <span role="columnheader">计算量</span>
        </div>
        <div className="ai-candidate-table-body" role="rowgroup">
          {candidates.map((candidate) => (
            <button
              type="button"
              role="row"
              className={candidate.rank === selectedRank ? 'is-selected' : ''}
              key={candidate.rank}
              disabled={variationPending && candidate.rank !== selectedRank}
              aria-pressed={candidate.rank === selectedRank}
              aria-label={`第${candidate.rank}选点，胜率${(candidate.winRateBps / 100).toFixed(1)}%，目差${formatAiScoreLead(candidate.scoreLeadHalfPoints)}目，计算量${formatAiVisits(candidate.visits)}`}
              onClick={() => onSelect(candidate.rank)}
            >
              <span role="cell">
                <i
                  style={
                    {
                      '--ai-candidate-fill': aiCandidateAppearance(candidate.rank).fill,
                      '--ai-candidate-text': aiCandidateAppearance(candidate.rank).text,
                      '--ai-candidate-stroke': aiCandidateAppearance(candidate.rank).stroke,
                    } as CSSProperties
                  }
                >
                  {candidate.rank}
                </i>
              </span>
              <strong role="cell">{(candidate.winRateBps / 100).toFixed(1)}%</strong>
              <span role="cell">{formatAiScoreLead(candidate.scoreLeadHalfPoints)}</span>
              <span role="cell">{formatAiVisits(candidate.visits)}</span>
            </button>
          ))}
          {candidates.length === 0 ? <p>正在等待首批推荐点…</p> : null}
        </div>
      </div>
      {selectedRank !== null ? <footer>已显示第 {selectedRank} 选点，再次点击取消</footer> : null}
    </aside>
  );
}
